import { useTranslations, cx, useTabIsActive } from "@jugl-web/utils";
import { SubscriptionPlanModuleId } from "@jugl-web/rest-api";
import {
  PlainButton,
  Button,
  MultiSectionLayout,
} from "@jugl-web/ui-components";
import { useWorkspaceFeatureInfo } from "@web-src/modules/workspace/hooks/useWorkspaceFeatureInfo/useWorkspaceFeatureInfo";
import { useEffect, useState, useRef } from "react";
import { ReactComponent as CloseIcon } from "./assets/close.svg";

export const ModuleInfoSection = ({
  initialFeature,
  config,
  onClose,
}: {
  initialFeature: SubscriptionPlanModuleId | null;
  config: Record<SubscriptionPlanModuleId, boolean>;
  onClose: (closeParentModal?: boolean) => void;
}) => {
  const { t } = useTranslations();
  const isTabActive = useTabIsActive();
  const videoPlayerRef = useRef<HTMLVideoElement | null>(null);
  const [currentModule, setCurrentModule] =
    useState<SubscriptionPlanModuleId | null>(initialFeature);
  const featuresData = useWorkspaceFeatureInfo();
  const enabledModules = Object.keys(config).filter(
    (key) => config[key as SubscriptionPlanModuleId]
  ) as SubscriptionPlanModuleId[];

  const featureInfo = currentModule && featuresData[currentModule];
  const handlePrevModule = () => {
    if (!currentModule) return;
    const currentIndex = enabledModules.indexOf(currentModule);
    const prevIndex = currentIndex - 1;
    const prevModule =
      prevIndex >= 0
        ? enabledModules[prevIndex]
        : enabledModules[enabledModules.length - 1];
    setCurrentModule(prevModule);
  };

  const handleNextModule = () => {
    if (!currentModule) return;
    const currentIndex = enabledModules.indexOf(currentModule);
    const nextIndex = currentIndex + 1;
    if (nextIndex === enabledModules.length) {
      onClose(true);
      return;
    }
    const nextModule =
      nextIndex < enabledModules.length
        ? enabledModules[nextIndex]
        : enabledModules[0];
    setCurrentModule(nextModule);
  };

  useEffect(() => {
    if (initialFeature) {
      setCurrentModule(initialFeature);
    }
  }, [initialFeature]);

  useEffect(() => {
    if (!isTabActive) {
      if (videoPlayerRef.current) {
        videoPlayerRef.current.pause();
      }
    }
  }, [isTabActive]);

  return (
    <div
      className={cx(
        "showcase-content-container relative flex h-full w-full flex-col justify-between overflow-x-hidden font-secondary max-lg:justify-center",
        {
          hidden: initialFeature === null,
        }
      )}
    >
      <MultiSectionLayout
        header={
          <div
            className={cx(
              "flex h-[60px] min-h-[60px] w-full items-center justify-between border-0 border-b-[1px] border-solid border-dark-100 px-[24px]"
            )}
          >
            <div className="flex h-full items-center gap-3 text-lg font-semibold text-[#383838]">
              {featureInfo?.titleIcon}
              {featureInfo?.title}
            </div>
            <PlainButton onClick={() => onClose(false)}>
              <CloseIcon />
            </PlainButton>
          </div>
        }
        footer={
          <div className="flex h-[77px] flex-1 items-center justify-between border-0 border-t-[2px] border-solid border-dark-100 px-10">
            <Button
              color="grey"
              className="h-[40px] font-secondary text-sm font-normal text-dark-700"
              onClick={() => onClose(false)}
            >
              {t({
                id: "workspace-features-showcase.back-to-list",
                defaultMessage: "Back to list",
              })}
            </Button>
            <div className="flex gap-4">
              {currentModule && enabledModules.indexOf(currentModule) !== 0 && (
                <Button
                  color="primary"
                  className="h-[40px] font-normal"
                  onClick={handlePrevModule}
                >
                  Previous Module
                </Button>
              )}
              <Button
                color="primary"
                className="h-[40px] font-normal"
                onClick={handleNextModule}
              >
                {currentModule &&
                enabledModules.indexOf(currentModule) ===
                  enabledModules.length - 1
                  ? "Explore Workspace"
                  : "Next Module"}
              </Button>
            </div>
          </div>
        }
        classNames={{ content: "flex" }}
      >
        <div className="my-auto flex w-full flex-col justify-between p-5 2xl:h-[646px] 2xl:p-10">
          {initialFeature && (
            <video
              key={videoPlayerRef.current?.src} // to reset video interface when src changes
              className="aspect-[2.17] w-full rounded-xl object-cover object-top"
              src={featureInfo?.steps[1]?.videoUrl}
              controls
              disablePictureInPicture
              controlsList="nodownload noplaybackrate"
              poster={featureInfo?.steps[1]?.thumbnailUrl}
              ref={videoPlayerRef}
            />
          )}
        </div>
      </MultiSectionLayout>
    </div>
  );
};
